import { useQuery } from '@tanstack/vue-query';
import type { ServiceListItem, ServiceDetail } from '@/types/services';
import { useAuthStore } from '@/store/auth';
import { queries } from '~/keys';

export const useServices = () => {
  const { currentLocale } = useLocaleSwitch();

  return useQuery({
    ...queries.services.list(currentLocale),
    queryFn: () =>
      $fetch<{ success: boolean; data: ServiceListItem[] }>('/api/services', {
        query: { locale: currentLocale.value },
      }),
    select: (data) => data.data,
    staleTime: 1000 * 60 * 5,
    retry: false,
  });
};

export const useUserServices = () => {
  const authStore = useAuthStore();

  return useQuery({
    queryKey: ['user-services', authStore.token],
    queryFn: async () => {
      if (!authStore.token) {
        return [];
      }
      try {
        const response = await $fetch<{ success: boolean; data: (ServiceListItem & { usedCount: number })[] }>(
          '/api/user/services',
          {
            headers: {
              Authorization: `Bearer ${authStore.token}`,
            },
          },
        );

        const data = typeof response === 'string' ? JSON.parse(response) : response;
        return data?.data || [];
      } catch (error: any) {
        if (error.status === 401 || error.statusCode === 401) {
          return [];
        }
        throw error;
      }
    },
    enabled: !!authStore.token,
    staleTime: 0,
    retry: false,
  });
};

export const useServiceDetail = (serviceId: string) => {
  const { currentLocale } = useLocaleSwitch();

  return useQuery({
    ...queries.services.detail(serviceId, currentLocale),
    queryFn: () =>
      $fetch<{ success: boolean; data: ServiceDetail }>(`/api/services/${serviceId}`, {
        query: { locale: currentLocale.value },
      }),
    select: (data) => data.data,
    enabled: !!serviceId,
    staleTime: 1000 * 60 * 10,
    retry: false,
  });
};
